/**
 * #978 — the ONE note panel a page mounts (via `CardNotesProvider`).
 *
 * Page-level rather than per-card so a draft outlives its card: when the order
 * fills or the position closes mid-edit, the text stays here to copy and Save
 * is refused (never retargeted). All editor state lives in the controller, not
 * in this component, so market ticks re-rendering the cards cannot remount or
 * reset an edit.
 *
 * ─── Isolation from trading ──────────────────────────────────────────────────
 *
 * The panel is portalled, but React events still bubble through portals to
 * the React parent. The root therefore stops click, mousedown and keydown: a
 * keystroke in the note can never reach a card's Enter/Space activation or a
 * hotkey listener, and a click can never open a ticket. `role="dialog"` also
 * places it under the LT2 key-owner selector wherever LT2 is mounted.
 *
 * ─── Dismissal ───────────────────────────────────────────────────────────────
 *
 * Close, X, Escape and an outside click all go through `requestClose`: with
 * unsaved text they raise the inline "Discard changes?" choice instead of
 * closing. No focusout dismissal — tabbing away must not cost a draft. Focus
 * returns to the originating control on keyboard/explicit closes (else the
 * section heading when that card is gone); an outside click leaves focus where
 * the trader put it, matching `AlertControlsMenu`.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useStore } from 'zustand';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';
import { isDirty, type CardNotesController } from '@/lib/cardNotes/cardNotesController';
import { NOTE_MAX_CODE_POINTS, codePointLength, isOverLimit, noteForSave } from '@/lib/cardNotes/noteText';
import {
  NOTE_PANEL_WIDTH,
  formatEditedAt,
  notePanelPlacement,
  samePlacement,
  type NotePanelPlacement,
} from './notePanelPlacement';

/** Show the counter once the draft is this close to the limit. */
const COUNTER_FROM = NOTE_MAX_CODE_POINTS - 400;

/** Shown in view and edit mode. ASCII only. */
export const NOTE_DISCLOSURE = 'Your managers and admins can read your notes. Only you can edit them.';

const BTN =
  'rounded px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60 disabled:cursor-not-allowed disabled:opacity-50';
const BTN_SECONDARY = `${BTN} border border-border text-foreground hover:bg-muted`;
const BTN_PRIMARY = `${BTN} bg-purple-500/80 text-white hover:bg-purple-500`;

function focusEl(el: HTMLElement | null | undefined): boolean {
  if (!el || !el.isConnected) return false;
  if (!el.matches('button, [href], input, textarea, select, [tabindex]')) el.setAttribute('tabindex', '-1');
  el.focus();
  return true;
}

export function CardNotePanel({ controller }: { controller: CardNotesController }) {
  const editor = useStore(controller.store, (s) => s.editor);
  const entry = useStore(controller.store, (s) => (s.editor ? s.entries[s.editor.key] : undefined));
  const present = useStore(controller.store, (s) => (s.editor ? (s.presence[s.editor.key] ?? 0) > 0 : false));

  const panelRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const originRef = useRef<HTMLElement | null>(null);
  /** Captured at open: where focus goes if the originating card is gone. */
  const headingRef = useRef<HTMLElement | null>(null);
  const [placement, setPlacement] = useState<NotePanelPlacement>({ mode: 'contained', maxHeight: 0 });

  const openKey = editor?.key ?? null;
  const originAnchorId = editor?.originAnchorId ?? null;

  const returnFocus = useCallback(() => {
    if (!focusEl(originRef.current)) focusEl(headingRef.current);
  }, []);

  const reposition = useCallback(() => {
    if (!openKey) return;
    const origin = originAnchorId ? controller.anchorElement(originAnchorId) : null;
    // The clicked copy, else any other mounted copy of the same target.
    const anchor = origin ?? controller.anyAnchorFor(openKey);
    originRef.current = anchor;
    const card = anchor?.closest<HTMLElement>('[data-card-root]') ?? anchor;
    const next = notePanelPlacement(card?.getBoundingClientRect() ?? null, {
      width: window.innerWidth,
      height: window.innerHeight,
    });
    setPlacement((prev) => (samePlacement(prev, next) ? prev : next));
  }, [controller, originAnchorId, openKey]);

  // Re-place on open, on origin change and whenever presence flips (the card
  // left, or came back). Measuring the card's rect is a DOM read that can only
  // happen after commit; doing it in a layout effect lands the placement before
  // paint, so the panel never flashes at a stale position.
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    reposition();
  }, [reposition, present]);

  useEffect(() => {
    if (!openKey) return;
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [openKey, reposition]);

  // Focus on open; capture the heading fallback while the card still exists.
  useEffect(() => {
    if (!openKey) return;
    headingRef.current =
      originRef.current?.closest('section')?.querySelector<HTMLElement>('h1, h2, h3') ?? null;
    panelRef.current?.focus();
  }, [openKey]);

  const mode = editor?.mode;
  useEffect(() => {
    if (mode !== 'edit') return;
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
  }, [mode, openKey]);

  const confirming = editor?.confirmingDiscard ?? false;
  const keepRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) keepRef.current?.focus();
  }, [confirming]);

  const closeWithFocus = () => {
    if (controller.requestClose()) returnFocus();
  };

  useDismissibleLayer({
    open: openKey != null,
    onDismiss: (reason) => {
      if (reason === 'escape') closeWithFocus();
      else controller.requestClose();
    },
    refs: [panelRef, originRef],
  });

  if (!editor) return null;

  const record = entry?.status === 'ready' ? entry.record : null;
  const dirty = isDirty(editor);
  const over = isOverLimit(editor.draft);
  const length = codePointLength(editor.draft.trim());
  const clearing = editor.mode === 'edit' && noteForSave(editor.draft) == null && editor.baseNote !== '';

  const style =
    placement.mode === 'anchored'
      ? { top: placement.top, left: placement.left, maxHeight: placement.maxHeight, width: NOTE_PANEL_WIDTH }
      : {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxHeight: placement.maxHeight,
          width: `min(${NOTE_PANEL_WIDTH}px, calc(100vw - 16px))`,
        };

  let body: ReactNode;
  if (editor.mode === 'view') {
    body =
      entry?.status === 'error' ? (
        <Notice tone="error">
          Couldn’t load this note.{' '}
          <button type="button" className="underline" onClick={() => controller.retryRead(editor.key)}>
            Retry
          </button>
        </Notice>
      ) : entry?.status === 'unavailable' ? (
        <Notice tone="error">{"This card's account could not be confirmed, so its note is unavailable."}</Notice>
      ) : !record ? (
        <p className="text-muted-foreground text-xs">Loading note…</p>
      ) : record.note ? (
        <div
          data-card-note-text=""
          className="border-border bg-muted/30 text-foreground max-h-60 overflow-y-auto rounded-md border px-2.5 py-2 text-xs whitespace-pre-wrap [overflow-wrap:anywhere]"
        >
          {record.note}
        </div>
      ) : (
        <p className="text-muted-foreground text-xs">No note yet.</p>
      );
  } else {
    body = (
      <>
        <textarea
          ref={textareaRef}
          aria-label="Trader note"
          value={editor.draft}
          readOnly={editor.saving}
          onChange={(e) => controller.setDraft(e.target.value)}
          rows={6}
          className="border-border bg-background text-foreground focus:ring-purple-400/50 w-full resize-y rounded-md border px-2.5 py-2 text-xs [overflow-wrap:anywhere] focus:ring-2 focus:outline-none"
        />
        <div className="mt-1 flex items-center justify-between gap-2 text-[11px]">
          <span className="text-muted-foreground">{clearing ? 'Saving an empty note clears it.' : ''}</span>
          {(length >= COUNTER_FROM || over) && (
            <span className={over ? 'text-negative font-medium' : 'text-muted-foreground'}>
              {length.toLocaleString()} / {NOTE_MAX_CODE_POINTS.toLocaleString()}
            </span>
          )}
        </div>
      </>
    );
  }

  const issue = editor.issue;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label="Trader note"
      data-lt2-owner=""
      data-card-note-panel={placement.mode}
      tabIndex={-1}
      style={style}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key !== 'Escape') return;
        e.preventDefault();
        if (editor.confirmingDiscard) controller.keepEditing();
        else closeWithFocus();
      }}
      className="bg-card fixed z-[100] flex flex-col overflow-y-auto rounded-lg border border-purple-400/60 p-3 shadow-lg focus:outline-none"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-foreground text-sm font-semibold">Trader Note</h2>
        <button
          type="button"
          onClick={closeWithFocus}
          disabled={editor.saving}
          aria-label="Close note"
          className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-purple-400/60 shrink-0 rounded p-1 transition-colors focus:outline-none focus-visible:ring-2 disabled:opacity-50"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>

      {/* R2: managers and admins can READ a trader's notes (never edit them), so the trader is told. */}
      <p data-card-note-disclosure="" className="text-muted-foreground mb-2 text-[11px]">
        {NOTE_DISCLOSURE}
      </p>

      {!present && (
        <Notice tone="warn">
          This card is no longer on screen. Saving is off so this note can’t land on another card
          {editor.mode === 'edit' ? ' — copy your text to keep it.' : '.'}
          {editor.mode === 'edit' && <CopyDraft text={editor.draft} />}
        </Notice>
      )}

      {body}

      {record?.updatedAt && editor.mode === 'view' && (
        <p className="text-muted-foreground mt-2 text-[11px]" title={record.updatedAt}>
          Last edited {formatEditedAt(record.updatedAt)}
        </p>
      )}

      {issue?.kind === 'failed' && (
        <Notice tone="error">Couldn’t save. Your text is still here — try again.</Notice>
      )}
      {issue?.kind === 'forbidden' && (
        <Notice tone="error">You don’t have permission to edit this note.</Notice>
      )}
      {issue?.kind === 'conflict' && (
        <Notice tone="warn">
          This note changed somewhere else after you opened it. Your text is kept.
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className={BTN_SECONDARY} onClick={() => controller.cancelEdit()}>
              Discard mine, show latest
            </button>
            <button
              type="button"
              className={BTN_PRIMARY}
              disabled={!present || over}
              onClick={() => void controller.saveOverLatest()}
            >
              Save mine over it
            </button>
          </div>
        </Notice>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {editor.confirmingDiscard ? (
          <>
            <span className="text-foreground mr-auto text-xs">Discard unsaved changes?</span>
            <button ref={keepRef} type="button" className={BTN_SECONDARY} onClick={() => controller.keepEditing()}>
              Keep editing
            </button>
            <button
              type="button"
              className={`${BTN} bg-red-500/80 text-white hover:bg-red-500`}
              onClick={() => {
                controller.discardAndClose();
                returnFocus();
              }}
            >
              Discard
            </button>
          </>
        ) : editor.mode === 'view' ? (
          <>
            <button
              type="button"
              className={BTN_SECONDARY}
              disabled={!record || !present}
              onClick={() => controller.startEdit()}
            >
              Edit
            </button>
            <button type="button" className={BTN_PRIMARY} onClick={closeWithFocus}>
              Close
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={BTN_SECONDARY}
              disabled={editor.saving}
              onClick={() => controller.cancelEdit()}
            >
              Cancel
            </button>
            <button
              type="button"
              className={BTN_PRIMARY}
              disabled={editor.saving || !dirty || over || !present || issue?.kind === 'conflict'}
              onClick={() => void controller.save()}
            >
              {editor.saving ? 'Saving…' : 'Save'}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function Notice({ tone, children }: { tone: 'warn' | 'error'; children: ReactNode }) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`mb-2 rounded-md border px-2.5 py-1.5 text-[11px] leading-snug ${
        tone === 'error'
          ? 'border-red-500/40 bg-red-500/10 text-red-300 light:text-red-700'
          : 'border-amber-400/40 bg-amber-400/10 text-amber-200 light:text-amber-800'
      }`}
    >
      {children}
    </div>
  );
}

function CopyDraft({ text }: { text: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <button
        type="button"
        className={BTN_SECONDARY}
        onClick={() => {
          const write = navigator.clipboard?.writeText(text);
          if (!write) return setState('failed');
          write.then(
            () => setState('copied'),
            () => setState('failed'),
          );
        }}
      >
        Copy text
      </button>
      {state === 'copied' && <span>Copied.</span>}
      {state === 'failed' && <span>Couldn’t copy — select the text and copy it manually.</span>}
    </div>
  );
}
